<script>
  import { onMount, onDestroy } from 'svelte';
  import { api } from '../lib/api.js';

  // Live site visits — one row per NON-ad visit (one device for one day), from the same cookieless
  // beacon log as Ad visits (/api/analytics/site-visits). Polls every 20 s while the tab is visible.
  let data = null, loading = true, error = null;
  let range = 'today', kind = 'all', hideBounces = false, hideInternal = true, expanded = null;
  let timer = null;

  async function load() {
    loading = true; error = null;
    try { data = await api(`/analytics/site-visits?range=${range}`); }
    catch (e) { error = e.message; } finally { loading = false; }
  }
  function setRange(r) { range = r; expanded = null; load(); }
  onMount(() => {
    load();
    timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 20000);
  });
  onDestroy(() => clearInterval(timer));

  const STAGE_LABEL = { landed: 'landed', product: 'product page', builder: 'opened builder', personalised: 'personalised',
    checkout_try: 'tried add to cart', cart: 'in basket', ordered: 'ordered' };
  const STEP_LABEL = { open: 'opened builder', design: 'changed design', card_opened: 'opened card', write_cue: 'tapped “Write your message”', message: 'typed message',
    long_message: 'long message (card will scroll)',
    handwriting: 'uploaded handwriting', delivery: 'recipient details', atc_try: 'add to cart', atc_blocked: 'blocked',
    atc_error: 'save error', cart: 'sent to basket' };
  const fmtClock = (s) => (s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—');
  const fmtWhen = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    const today = new Date().toDateString() === d.toDateString();
    return today ? fmtClock(s) : d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const fmtDur = (ms) => { if (!ms) return '—'; const s = Math.round(ms / 1000); return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`; };
  const ago = (s) => { const m = Math.round((Date.now() - new Date(s)) / 60000); return m < 1 ? 'now' : m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`; };
  const isLive = (v) => Date.now() - new Date(v.lastAt) < 5 * 60000;
  const shortPath = (p) => (p || '').replace(/^\/collections\/[^/]+\/products\//, '/products/').replace(/^\/products\//, '').replace(/^\/collections\//, 'coll: ') || '/';
  const funnelPct = (s, stage) => (s ? (s.funnel.find((f) => f.stage === stage) || {}).pct : null);
  const summariseRows = (rs) => {
    // Client-side summary for the current filter (mirrors the server's summarise).
    const n = rs.length;
    const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);
    const med = (xs) => { if (!xs.length) return null; const s = xs.slice().sort((a, b) => a - b); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
    const stages = data ? data.stages : [];
    const reached = (st) => rs.filter((r) => r.stageIndex >= stages.indexOf(st)).length;
    const ma = med(rs.map((r) => r.activeMs));
    return { visits: n, bounceRate: pct(rs.filter((r) => r.bounced).length, n), medianPages: med(rs.map((r) => r.pages)),
      medianActiveSec: ma == null ? null : Math.round(ma / 1000), funnel: stages.map((st) => ({ stage: st, count: reached(st), pct: pct(reached(st), n) })),
      orders: rs.filter((r) => r.order).length };
  };

  $: visible = data ? data.visits.filter((v) => !v.bot && (!hideInternal || v.sourceKind !== 'internal') && (kind === 'all' || v.sourceKind === kind)) : [];
  $: rows = visible.filter((v) => !hideBounces || !v.bounced);
  $: sums = data ? summariseRows(visible) : null;
  $: kindRows = data ? data.byKind.filter((k) => (!hideInternal || k.kind !== 'internal') && (kind === 'all' || k.kind === kind)) : [];
  $: srcRows = data ? data.bySource.filter((s) => (!hideInternal || s.kind !== 'internal') && (kind === 'all' || s.kind === kind)) : [];
  $: landRows = data ? (() => {
    const m = new Map();
    for (const v of visible) { const k = v.landing || '/'; if (!m.has(k)) m.set(k, []); m.get(k).push(v); }
    return [...m.entries()].map(([landing, rs]) => ({ landing, ...summariseRows(rs) })).sort((a, b) => b.visits - a.visits).slice(0, 25);
  })() : [];
  $: devRows = data ? ['m', 'd'].map((dv) => ({ device: dv, ...summariseRows(visible.filter((v) => v.device === dv)) })).filter((d) => d.visits) : [];
  $: liveNow = visible.filter(isLive).length;
  $: internalCount = data ? data.visits.filter((v) => !v.bot && v.sourceKind === 'internal').length : 0;
  const kindClass = (k) => `kind ${k || 'direct'}`;
</script>

<section>
  <div class="head">
    <h2>Live site visits <small>everyone who did not arrive from an ad · cookieless · updates every 20 s</small></h2>
    <div class="ctrl">
      {#each [['today', 'Today'], ['24h', '24 h'], ['7d', '7 days'], ['28d', '28 days']] as [r, l]}
        <button class:on={range === r} on:click={() => setRange(r)}>{l}</button>
      {/each}
      <select bind:value={kind}>
        <option value="all">All sources</option>
        {#if data}{#each data.kinds as k}<option value={k.kind}>{k.label}</option>{/each}{/if}
      </select>
      <label><input type="checkbox" bind:checked={hideBounces} /> hide bounces</label>
      <label title="Visits whose first beacon came from one of our own pages (no real entry seen)"><input type="checkbox" bind:checked={hideInternal} /> hide internal</label>
      <span class="meta">{#if loading}loading…{:else if data}updated {fmtClock(data.generatedAt)}{/if}</span>
    </div>
  </div>

  {#if error}<p class="error">{error}</p>{/if}

  {#if data && sums}
    <div class="cards">
      <div class="card"><span class="label">Site visits</span><span class="value">{sums.visits}</span><span class="sub">{liveNow} active in the last 5 min{#if data.bots} · {data.bots} bot{data.bots === 1 ? '' : 's'} excluded{/if}{#if hideInternal && internalCount} · {internalCount} internal hidden{/if}</span></div>
      <div class="card"><span class="label">Bounced</span><span class="value" class:bad={sums.bounceRate >= 60}>{sums.bounceRate ?? '—'}%</span><span class="sub">1 page, no builder, under 10 s</span></div>
      <div class="card"><span class="label">Median pages · time</span><span class="value">{sums.medianPages ?? '—'} · {sums.medianActiveSec != null ? fmtDur(sums.medianActiveSec * 1000) : '—'}</span><span class="sub">store pages; time = tab visible</span></div>
      <div class="card"><span class="label">Opened builder</span><span class="value">{funnelPct(sums, 'builder') ?? '—'}%</span><span class="sub">personalised {funnelPct(sums, 'personalised') ?? '—'}% · basket {funnelPct(sums, 'cart') ?? '—'}%</span></div>
      <div class="card"><span class="label">Ordered</span><span class="value">{sums.orders}</span><span class="sub">{sums.visits ? Math.round(sums.orders / sums.visits * 1000) / 10 : 0}% of visits</span></div>
    </div>

    <div class="funnel">
      {#each sums.funnel as f}
        <div class="fstep" title={`${f.count} of ${sums.visits}`}>
          <div class="fbar"><div class="ffill" style={`width:${f.pct || 0}%`}></div></div>
          <span class="flab">{STAGE_LABEL[f.stage]}</span><span class="fnum">{f.count} <small>{f.pct ?? 0}%</small></span>
        </div>
      {/each}
    </div>

    {#if kindRows.length > 1 || srcRows.length > 1}
      <h3>Where they came from <small>same range · bots excluded</small></h3>
      <div class="two">
        <div class="tablewrap"><table class="mini">
          <thead><tr><th>Channel</th><th class="num">Visits</th><th class="num">Bounce</th><th class="num">Med. pages</th><th class="num">Builder</th><th class="num">Basket</th><th class="num">Orders</th></tr></thead>
          <tbody>
            {#each kindRows as k}
              <tr><td><span class={kindClass(k.kind)}>{k.label}</span></td><td class="num">{k.visits}</td><td class="num">{k.bounceRate}%</td><td class="num">{k.medianPages}</td><td class="num">{funnelPct(k, 'builder')}%</td><td class="num">{funnelPct(k, 'cart')}%</td><td class="num" class:good={k.orders}>{k.orders}</td></tr>
            {/each}
          </tbody>
        </table></div>
        <div class="tablewrap"><table class="mini">
          <thead><tr><th>Source</th><th class="num">Visits</th><th class="num">Bounce</th><th class="num">Med. pages</th><th class="num">Builder</th><th class="num">Basket</th><th class="num">Orders</th></tr></thead>
          <tbody>
            {#each srcRows.slice(0, 15) as s}
              <tr><td>{s.source}</td><td class="num">{s.visits}</td><td class="num">{s.bounceRate}%</td><td class="num">{s.medianPages}</td><td class="num">{funnelPct(s, 'builder')}%</td><td class="num">{funnelPct(s, 'cart')}%</td><td class="num" class:good={s.orders}>{s.orders}</td></tr>
            {/each}
          </tbody>
        </table></div>
      </div>
    {/if}

    {#if devRows.length}
      <h3>By device <small>same range · bots excluded</small></h3>
      <div class="tablewrap"><table class="mini">
        <thead><tr><th>Device</th><th class="num">Visits</th><th class="num">Bounce</th><th class="num">Med. pages</th><th class="num">Med. time</th><th class="num">Builder</th><th class="num">Personalised</th><th class="num">Basket</th><th class="num">Orders</th><th class="num">Visit → order</th></tr></thead>
        <tbody>
          {#each devRows as d}
            <tr><td>{d.device === 'm' ? 'Phone' : 'Desktop'}</td><td class="num">{d.visits}</td><td class="num">{d.bounceRate}%</td><td class="num">{d.medianPages}</td><td class="num">{fmtDur(d.medianActiveSec * 1000)}</td><td class="num">{funnelPct(d, 'builder')}%</td><td class="num">{funnelPct(d, 'personalised')}%</td><td class="num">{funnelPct(d, 'cart')}%</td><td class="num">{d.orders}</td><td class="num">{d.visits ? (d.orders / d.visits * 100).toFixed(1) : '—'}%</td></tr>
          {/each}
        </tbody>
      </table></div>
    {/if}

    <h3>Visits</h3>
    {#if !rows.length}
      <p class="empty">No site visits {range === 'today' ? 'yet today' : 'in this range'}.</p>
    {:else}
      <div class="tablewrap">
        <table class="visits">
          <thead><tr><th>Arrived</th><th>Source · device</th><th>Landed on</th><th class="num">Pages</th><th class="num">Time</th><th class="num">Scroll</th><th>Furthest</th><th>Last seen</th></tr></thead>
          <tbody>
            {#each rows as v (v.id)}
              <tr class:bounced={v.bounced} class:ordered={v.order} on:click={() => (expanded = expanded === v.id ? null : v.id)}>
                <td class="t">{fmtWhen(v.firstAt)}{#if v.returns}<br /><small class="grp">+{v.returns} return{v.returns === 1 ? '' : 's'}</small>{/if}</td>
                <td class="t"><span class={kindClass(v.sourceKind)}>{v.source}</span>{#if v.campaign || v.ref}<br /><small class="grp">{v.campaign || ''}{v.campaign && v.ref ? ' · ' : ''}{v.ref ? `ref=${v.ref}` : ''}</small>{/if}{#if v.device}<br /><small class="grp">{v.device === 'm' ? 'phone' : 'desktop'}</small>{/if}</td>
                <td class="path" title={v.landing}>{shortPath(v.landing)}</td>
                <td class="num">{v.pages}</td>
                <td class="num">{fmtDur(v.activeMs)}</td>
                <td class="num">{v.maxScroll ? `${v.maxScroll}%` : '—'}</td>
                <td><span class={`stage s${v.stageIndex}`}>{v.order ? `ordered ${v.order.number}` : STAGE_LABEL[v.stage]}</span>{#if v.bounced}<span class="chip bad">bounce</span>{/if}{#if v.blocked.length}<span class="chip warn" title="add-to-cart blocked on">blocked: {v.blocked.join(', ')}</span>{/if}</td>
                <td class="t">{#if isLive(v)}<span class="live">● live</span>{:else}{ago(v.lastAt)}{/if}</td>
              </tr>
              {#if expanded === v.id}
                <tr class="detail"><td colspan="8">
                  <ol class="trail">
                    {#each [...v.views.map((x) => ({ at: x.at, kind: 'view', x })), ...v.stepLog.map((x) => ({ at: x.at, kind: 'step', x })), ...v.entries.slice(1).map((x) => ({ at: x.at, kind: 'entry', x }))].sort((a, b) => a.at.localeCompare(b.at)) as e}
                      <li>
                        <span class="d">{new Date(e.at).toLocaleTimeString('en-GB')}</span>
                        {#if e.kind === 'view'}
                          <b>{e.x.type === 'builder' ? 'builder' : e.x.path}</b> <small class="grp">{e.x.type}{e.x.ms ? ` · ${fmtDur(e.x.ms)}` : ''}{e.x.scroll ? ` · scrolled ${e.x.scroll}%` : ''}</small>
                        {:else if e.kind === 'entry'}
                          <i>came back{e.x.host ? ` from ${e.x.host}` : ''}</i>
                        {:else}
                          → {STEP_LABEL[e.x.step] || e.x.step}{#if e.x.reason} <small class="grp">({e.x.reason})</small>{/if}
                        {/if}
                      </li>
                    {/each}
                    {#if v.order}<li><span class="d">{new Date(v.order.at).toLocaleTimeString('en-GB')}</span> <b>ordered {v.order.number}</b>{#if v.order.total} <small class="grp">£{v.order.total.toFixed(2)}</small>{/if}</li>{/if}
                  </ol>
                  <small class="grp">Referrer {v.referrerHost || 'none'}{#if v.medium} · medium {v.medium}{/if}{#if v.utmSource} · utm_source {v.utmSource}{/if}</small>
                </td></tr>
              {/if}
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    {#if landRows.length}
      <h3>Landing pages <small>same range · bots excluded · top 25</small></h3>
      <div class="tablewrap">
        <table class="mini">
          <thead><tr><th>Landed on</th><th class="num">Visits</th><th class="num">Bounce</th><th class="num">Med. pages</th><th class="num">Med. time</th><th class="num">Product</th><th class="num">Builder</th><th class="num">Basket</th><th class="num">Orders</th></tr></thead>
          <tbody>
            {#each landRows as l}
              <tr>
                <td class="path" title={l.landing}>{shortPath(l.landing)}</td>
                <td class="num">{l.visits}</td><td class="num" class:bad={l.visits >= 3 && l.bounceRate >= 70}>{l.bounceRate}%</td>
                <td class="num">{l.medianPages}</td><td class="num">{fmtDur(l.medianActiveSec * 1000)}</td>
                <td class="num">{funnelPct(l, 'product')}%</td><td class="num">{funnelPct(l, 'builder')}%</td><td class="num">{funnelPct(l, 'cart')}%</td>
                <td class="num" class:good={l.orders}>{l.orders}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    <p class="sub-note">Recorded for every visitor who did not arrive from a paid ad (those are under Ad visits). A visit is one device for one day: the server hashes the visitor's IP address and browser with a secret that changes daily, so nothing is stored on their device, no id travels in the URL, and two days can't be joined. People sharing a connection and browser (an office, a household on one phone) merge into one visit, and someone whose IP changes mid-visit (phone leaving Wi-Fi) splits into two. A gap of 30+ minutes counts as a return within the same visit. Source comes from the referrer of the first page and any utm or ref tags; "Direct" also includes links from apps and emails that hide their referrer, and ad visitors who lost the tag by typing a URL. "Ordered" is set by the order webhook, via the ecard's saved visit key or the checkout IP and browser.{#if data.truncated} Showing the newest {data.visits.length} visits only.{/if}</p>
  {:else if loading}
    <p class="empty">Loading…</p>
  {/if}
</section>

<style>
  section { padding: 1rem 1.25rem 3rem; }
  .head { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: .5rem; }
  h2 { margin: 0; font-size: 1.2rem; }
  h2 small, h3 small { font-weight: 400; color: #888; font-size: .75rem; }
  h3 { margin: 1.4rem 0 .5rem; font-size: 1rem; }
  .ctrl { display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; font-size: .82rem; }
  .ctrl button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .2rem .6rem; border-radius: 999px; font-size: .78rem; }
  .ctrl button.on { background: #2a5db0; color: #fff; border-color: #2a5db0; }
  .ctrl select { font: inherit; font-size: .8rem; padding: .15rem .3rem; border: 1px solid #ccc; border-radius: 6px; }
  .meta { color: #888; margin-left: .5rem; }
  .error { color: #b00020; }
  .empty { color: #888; }
  .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: .75rem; margin: 1rem 0; }
  .card { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .7rem .9rem; display: flex; flex-direction: column; gap: .15rem; }
  .card .label { font-size: .75rem; color: #888; text-transform: uppercase; letter-spacing: .03em; }
  .card .value { font-size: 1.5rem; font-weight: 600; font-variant-numeric: tabular-nums; }
  .card .value.bad { color: #b00020; }
  .card .sub { font-size: .75rem; color: #888; }
  .funnel { display: grid; grid-template-columns: repeat(7, 1fr); gap: .5rem; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .7rem; }
  @media (max-width: 800px) { .funnel { grid-template-columns: repeat(2, 1fr); } }
  .fstep { display: flex; flex-direction: column; gap: .2rem; font-size: .75rem; }
  .fbar { height: 8px; background: #eef2f8; border-radius: 4px; overflow: hidden; }
  .ffill { height: 100%; background: #4a7dbd; }
  .flab { color: #666; }
  .fnum { font-weight: 600; font-variant-numeric: tabular-nums; }
  .fnum small { color: #888; font-weight: 400; }
  .two { display: grid; grid-template-columns: 1fr 1fr; gap: .75rem; }
  @media (max-width: 1000px) { .two { grid-template-columns: 1fr; } }
  .tablewrap { overflow-x: auto; }
  table { border-collapse: collapse; width: 100%; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; font-size: .82rem; }
  th { text-align: left; font-weight: 600; color: #666; font-size: .75rem; padding: .4rem .5rem; border-bottom: 1px solid #e5e5e5; }
  td { padding: .35rem .5rem; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
  table.mini { margin-top: .75rem; }
  table.visits tbody tr:not(.detail) { cursor: pointer; }
  table.visits tbody tr:not(.detail):hover td { background: #f7f9fc; }
  tr.bounced td { color: #999; }
  tr.ordered td { background: #f0faf2; }
  tr.detail td { background: #fafafa; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  td.good { color: #1c7a2e; font-weight: 600; }
  td.bad { color: #b00020; font-weight: 600; }
  .t { white-space: nowrap; font-variant-numeric: tabular-nums; }
  td.path { max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .kind { display: inline-block; padding: .05rem .45rem; border-radius: 4px; font-weight: 700; font-size: .72rem; color: #fff; background: #999; white-space: nowrap; }
  .kind.search { background: #1a73e8; }
  .kind.direct { background: #555; }
  .kind.social { background: #c2185b; }
  .kind.referral { background: #6a1b9a; }
  .kind.email, .kind.sms { background: #ef6c00; }
  .kind.ecard { background: #e84393; }
  .kind.campaign { background: #00796b; }
  .kind.internal { background: #bbb; color: #333; }
  .chip { font-size: .66rem; padding: .05rem .4rem; border-radius: 999px; background: #f0f0f0; color: #888; margin-left: .4rem; white-space: nowrap; }
  .chip.bad { background: #fde8e8; color: #b00020; }
  .chip.warn { background: #fff3e0; color: #b26a00; }
  .grp { color: #999; font-size: .72rem; }
  .stage { font-size: .75rem; padding: .05rem .45rem; border-radius: 999px; background: #f0f0f0; color: #666; white-space: nowrap; }
  .stage.s1 { background: #eef2f8; color: #2a5db0; }
  .stage.s2, .stage.s3 { background: #e8f0fe; color: #2a5db0; font-weight: 600; }
  .stage.s4, .stage.s5 { background: #fff3e0; color: #b26a00; font-weight: 600; }
  .stage.s6 { background: #e2f5e5; color: #1c7a2e; font-weight: 700; }
  .live { color: #1c7a2e; font-weight: 600; }
  .trail { margin: .2rem 0 .4rem; padding-left: 1.2rem; font-size: .8rem; }
  .trail li { padding: .1rem 0; }
  .trail .d { color: #888; font-variant-numeric: tabular-nums; margin-right: .4rem; }
  .sub-note { font-size: .75rem; color: #888; margin-top: 1rem; max-width: 70em; }
</style>
